import { FC, ReactNode, StrictMode, Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from 'react-query'
import { useBrandQuery, useBrandsQuery } from 'api/brands'
import { createVoluumCookie } from 'helpers/voluum-handling'
import Cookies from 'js-cookie'
import preval from 'preval.macro'
import { queryClient } from 'query'
import { brandsInfo } from 'constants/misc'
import { Spinner } from 'components/spinner'
import './index.css'

document.body.dataset.buildTimestamp = preval`module.exports = new Date().toUTCString();`
document.body.dataset.commitHash = preval`module.exports =  require('child_process').execSync('git rev-parse --short HEAD').toString().trim();`
document.body.dataset.version = preval`module.exports = require('../package.json').version`

const LazyWelcomePage = lazy(() => import('components/welcome-page'))
const LazyApp = lazy(() => import('components/app'))

const AppOrWelcomePage: FC = () => {
  const { data: brands, isLoading } = useBrandsQuery()
  const { data: brandData } = useBrandQuery()
  const findBrand = brandsInfo.find((brandInfo) => brandInfo.brandId === brandData?.brandId)

  let params = new URLSearchParams(document.location.search)

  let affiliateId, cid

  for (const [key, value] of params.entries()) {
    if (key.toLowerCase() === 'affiliateid') {
      affiliateId = value
    } else if (key.toLowerCase() === 'cid') {
      cid = value
    }
  }

  if (affiliateId) {
    Cookies.set('netReferAffiliateId', affiliateId, { expires: 365 })
  }

  if (cid && findBrand?.voluumIntegrationEnabled) {
    createVoluumCookie(cid)
  }

  const hasMatchingBrandRouteValue = useMemo(() => {
    if (!brands) return null

    return brands.some((brand) => brand.routingType === 'domain' && brand.routingValue === window.location.origin)
  }, [brands])

  if (!brands) {
    return isLoading ? (
      <div
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          display: 'flex',
        }}
      >
        <Spinner />
      </div>
    ) : null
  }

  return (
    <Suspense fallback={<Spinner />}>
      {!hasMatchingBrandRouteValue && window.location.pathname === '/' ? <LazyWelcomePage /> : <LazyApp />}
    </Suspense>
  )
}

// Conditional polyfill for very old devices used in Africa.
const PolyfillWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  const [init, setInit] = useState(false)

  useEffect(() => {
    if ('flat' in [] && 'replaceAll' in String.prototype) {
      setInit(true)
    } else {
      // @ts-ignore
      import('core-js/actual').then(() => {
        setInit(true)
      })
    }
  }, [])

  if (!init) return null

  return <>{children}</>
}

const container = document.getElementById('root')
const root = createRoot(container!)
root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <PolyfillWrapper>
        <AppOrWelcomePage />
      </PolyfillWrapper>
    </QueryClientProvider>
  </StrictMode>
)
